import type { ProfitLossSnapshot } from "../../shared/types";

export const mockProfitLoss = {
  asOfDate: "2026-05-31",
  periodLabel: "YTD 2026",
  currency: "CAD",
  overview: {
    revenue: {
      label: "Revenue",
      value: 184200000,
      unit: "currency",
      helper: "All stations"
    },
    grossProfit: {
      label: "Gross profit",
      value: 42800000,
      unit: "currency",
      helper: "23.2% gross margin"
    },
    operatingProfit: {
      label: "Operating profit",
      value: 19600000,
      unit: "currency",
      helper: "Gross profit less operating expenses"
    },
    netProfit: {
      label: "Net profit",
      value: 14300000,
      unit: "currency",
      helper: "7.8% net margin"
    },
    operatingExpenses: {
      label: "Operating expenses",
      value: 23200000,
      unit: "currency",
      helper: "12.6% of revenue"
    }
  },
  filters: {
    available: {
      months: [1, 2, 3, 4, 5],
      stations: [
        { label: "Bkejwanong Gen7 LP", shortName: "Bkejwanong", value: "30903" },
        { label: "Charlie's Gen7 LP", shortName: "Charlie's", value: "72560" },
        { label: "Freddies Gen7 LP", shortName: "Freddies", value: "30900" },
        { label: "FT Frances Gen7 LP", shortName: "FT Frances", value: "61327" },
        { label: "Nipissing Gen7 LP", shortName: "Nipissing", value: "30901" },
        { label: "Penticton Gen7 LP", shortName: "Penticton", value: "62182" },
        { label: "Smokeys Gen7 LP", shortName: "Smokeys", value: "30904" }
      ],
      years: [2026, 2025, 2024]
    },
    selected: {
      stationLabel: "All stations",
      year: 2026
    }
  },
  sankey: {
    nodes: [
      { id: "fuel-sales", label: "Fuel Sales", value: 112400000, level: 0, tone: "revenue", order: 1 },
      { id: "store-merch", label: "Store Merch.", value: 46800000, level: 0, tone: "revenue", order: 2 },
      { id: "commissions", label: "Commissions", value: 25000000, level: 0, tone: "revenue", order: 8 },
      { id: "revenue", label: "Revenue", value: 184200000, level: 1, tone: "revenue", order: 0 },
      { id: "gross-margin", label: "Gross Margin", value: 42800000, level: 2, tone: "profit", order: 0 },
      { id: "cost-of-revenue", label: "Cost of Revenue", value: 141400000, level: 2, tone: "cost", order: 1 },
      { id: "operating-profit", label: "Operating Profit", value: 19600000, level: 3, tone: "profit", order: 0 },
      { id: "operating-expenses", label: "Operating Expenses", value: 23200000, level: 3, tone: "expense", order: 1 },
      { id: "fuel-cost-retail", label: "Fuel Cost Retail", value: 94600000, level: 3, tone: "cost", order: 5 },
      { id: "tobacco", label: "Tobacco", value: 31800000, level: 3, tone: "cost", order: 7 },
      { id: "freight", label: "Freight & Brokerage", value: 15000000, level: 3, tone: "cost", order: 9 },
      { id: "net-income", label: "Net Income", value: 14300000, level: 4, tone: "profit", order: 0 },
      { id: "other-detail", label: "Other", value: 5300000, level: 4, tone: "expense", order: 1 },
      { id: "payroll-detail", label: "Payroll", value: 9200000, level: 4, tone: "expense", order: 8 },
      { id: "maintenance-detail", label: "Ops & Maint.", value: 3800000, level: 4, tone: "expense", order: 8 },
      { id: "ga-detail", label: "G & A", value: 4900000, level: 4, tone: "expense", order: 4 },
      { id: "facilities-detail", label: "Facilities", value: 5300000, level: 4, tone: "expense", order: 7 }
    ],
    links: [
      { source: "fuel-sales", target: "revenue", value: 112400000, tone: "neutral", label: "Fuel Sales to Revenue" },
      { source: "store-merch", target: "revenue", value: 46800000, tone: "neutral", label: "Store Merch. to Revenue" },
      { source: "commissions", target: "revenue", value: 25000000, tone: "neutral", label: "Commissions to Revenue" },
      { source: "revenue", target: "gross-margin", value: 42800000, tone: "profit", label: "Revenue to Gross Margin" },
      { source: "revenue", target: "cost-of-revenue", value: 141400000, tone: "cost", label: "Revenue to Cost of Revenue" },
      { source: "gross-margin", target: "operating-profit", value: 19600000, tone: "profit", label: "Gross Margin to Operating Profit" },
      { source: "gross-margin", target: "operating-expenses", value: 23200000, tone: "expense", label: "Gross Margin to Operating Expenses" },
      { source: "operating-profit", target: "net-income", value: 14300000, tone: "profit", label: "Operating Profit to Net Income" },
      { source: "operating-profit", target: "other-detail", value: 5300000, tone: "expense", label: "Operating Profit to Other" },
      { source: "cost-of-revenue", target: "fuel-cost-retail", value: 94600000, tone: "cost", label: "Cost of Revenue to Fuel Cost Retail" },
      { source: "cost-of-revenue", target: "tobacco", value: 31800000, tone: "cost", label: "Cost of Revenue to Tobacco" },
      { source: "cost-of-revenue", target: "freight", value: 15000000, tone: "cost", label: "Cost of Revenue to Freight & Brokerage" },
      { source: "operating-expenses", target: "payroll-detail", value: 9200000, tone: "expense", label: "Operating Expenses to Payroll" },
      { source: "operating-expenses", target: "maintenance-detail", value: 3800000, tone: "expense", label: "Operating Expenses to Ops & Maint." },
      { source: "operating-expenses", target: "ga-detail", value: 4900000, tone: "expense", label: "Operating Expenses to G & A" },
      { source: "operating-expenses", target: "facilities-detail", value: 5300000, tone: "expense", label: "Operating Expenses to Facilities" }
    ]
  },
  breakdown: [
    { label: "Revenue / Revenue - Sales / Fuel Sales", group: "revenue", value: 112400000, percentOfRevenue: 61.0 },
    { label: "Revenue / Revenue - Sales / Store Merch.", group: "revenue", value: 46800000, percentOfRevenue: 25.4 },
    { label: "Revenue / Revenue - Sales / Commissions", group: "revenue", value: 25000000, percentOfRevenue: 13.6 },
    { label: "Cost of Revenue / Cost of Sales Rev. / Fuel Cost Retail", group: "cost", value: 94600000, percentOfRevenue: 51.4 },
    { label: "Cost of Revenue / Cost of Sales Rev. / Tobacco", group: "cost", value: 31800000, percentOfRevenue: 17.3 },
    { label: "Cost of Revenue / Others / Freight & Brokerage", group: "cost", value: 15000000, percentOfRevenue: 8.1 },
    { label: "Operating Expenses / Payroll", group: "expense", value: 9200000, percentOfRevenue: 5.0 },
    { label: "Operating Expenses / Facilities", group: "expense", value: 5300000, percentOfRevenue: 2.9 },
    { label: "Operating Expenses / G & A", group: "expense", value: 4900000, percentOfRevenue: 2.7 },
    { label: "Operating Expenses / Ops & Maint.", group: "expense", value: 3800000, percentOfRevenue: 2.1 }
  ]
} satisfies ProfitLossSnapshot & { filters: unknown };
